import { makeAutoObservable } from "mobx";
import { regionsStore } from './index';
import { session_job_types } from '../utils/sessions';

class GlobalFiltersStore {
  // Состояние фильтров
  minLikes = '';
  minViews = '';
  minComments = '';
  startDate = '2025-01-01';
  endDate = null; // Будет установлена в конструкторе
  selectedGroups = [];
  selectedTimelineEventIds = [];
  selectedSession = session_job_types.reduce((acc, job_type) => ({ ...acc, [job_type]: '' }), {});
  
  // Состояние UI
  showDropdown = false;
  showExtra = false;
  tempSelectedGroups = [];

  // Константы
  DATE_MIN = '2015-01-01';
  DATE_MAX = null; // Будет вычислена динамически в конструкторе

  constructor() {
    // Вычисляем максимальную дату: текущая дата
    const today = new Date();
    this.DATE_MAX = today.toISOString().split('T')[0];
    
    // Устанавливаем текущую дату как дату окончания по умолчанию
    this.endDate = today.toISOString().split('T')[0];
    
    makeAutoObservable(this);
    this.loadFromLocalStorage();
  }

  // Actions для фильтров
  setMinLikes(value) {
    this.minLikes = value.replace(/[^0-9]/g, '');
    this.saveToLocalStorage();
  }

  setMinViews(value) {
    this.minViews = value.replace(/[^0-9]/g, '');
    this.saveToLocalStorage();
  }

  setMinComments(value) {
    this.minComments = value.replace(/[^0-9]/g, '');
    this.saveToLocalStorage();
  }

  setStartDate(date) {
    if (date <= this.DATE_MAX) {
      if(date < this.DATE_MIN)
        date = this.DATE_MIN
      this.startDate = date;
      this.saveToLocalStorage();
    }
  }

  setEndDate(date) {
    if (date >= this.DATE_MIN && date <= this.DATE_MAX) {
      this.endDate = date;
      this.saveToLocalStorage();
    }
  }

  setSelectedGroups(groups) {
    this.selectedGroups = Array.isArray(groups) ? groups : [];
    this.saveToLocalStorage();
  }

  setSelectedTimelineEventIds(eventIds) {
    this.selectedTimelineEventIds = Array.isArray(eventIds)
      ? [...new Set(eventIds.map(Number).filter(Number.isFinite))]
      : [];
    this.saveToLocalStorage();
  }

  toggleTimelineEvent(eventId) {
    const numericId = Number(eventId);
    if (!Number.isFinite(numericId)) return;
    if (this.selectedTimelineEventIds.includes(numericId)) {
      this.selectedTimelineEventIds = this.selectedTimelineEventIds.filter((id) => id !== numericId);
    } else {
      this.selectedTimelineEventIds = [...this.selectedTimelineEventIds, numericId];
    }
    this.saveToLocalStorage();
  }

  removeTimelineEvent(eventId) {
    const numericId = Number(eventId);
    this.selectedTimelineEventIds = this.selectedTimelineEventIds.filter((id) => id !== numericId);
    this.saveToLocalStorage();
  }

  // Actions для UI
  // Удалены методы setRegions и setIsGroupsLoading - теперь используется regionsStore

  setShowDropdown(show) {
    this.showDropdown = show;
  }

  setShowExtra(show) {
    this.showExtra = show;
  }

  setTempSelectedGroups(groups) {
    this.tempSelectedGroups = groups;
  }

  setSelectedSession(job_type, session_id) {
    this.selectedSession[job_type] = session_id;
    this.saveToLocalStorage();
  }

  getSelectedSession(job_type) {
    return this.selectedSession[job_type];
  }

  // Computed values
  get filters() {
    return {
      minLikes: this.minLikes,
      minViews: this.minViews,
      minComments: this.minComments,
      startDate: this.startDate,
      endDate: this.endDate,
      selectedGroups: this.selectedGroups,
      selectedSession: this.selectedSession
    };
  }

  get persistedFilters() {
    return {
      ...this.filters,
      selectedTimelineEventIds: this.selectedTimelineEventIds
    };
  }

  get hasActiveFilters() {
    return this.minLikes || this.minViews || this.minComments || 
           this.selectedGroups.length > 0 ||
           this.selectedTimelineEventIds.length > 0 ||
           this.startDate !== '2025-01-01' || 
           this.endDate !== this.DATE_MAX ||
           Object.values(this.selectedSession).some(session_id => session_id !== '');
  }

  get filtersString() {
    return JSON.stringify(this.filters);
  }

  // Persistence
  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem('globalFilters');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (this.validateFilters(parsed)) {
          this.minLikes = parsed.minLikes || '';
          this.minViews = parsed.minViews || '';
          this.minComments = parsed.minComments || '';
          this.startDate = parsed.startDate || '2025-01-01';
          this.endDate = parsed.endDate || this.DATE_MAX;
          this.selectedGroups = parsed.selectedGroups || [];
          this.selectedTimelineEventIds = Array.isArray(parsed.selectedTimelineEventIds)
            ? parsed.selectedTimelineEventIds.map(Number).filter(Number.isFinite)
            : [];
          this.selectedSession = parsed.selectedSession || session_job_types.reduce((acc, job_type) => ({ ...acc, [job_type]: '' }), {});
        }
      }
    } catch (error) {
      console.warn('Ошибка загрузки фильтров из localStorage:', error);
    }
  }

  saveToLocalStorage() {
    try {
      localStorage.setItem('globalFilters', JSON.stringify(this.persistedFilters));
    } catch (error) {
      console.warn('Ошибка сохранения фильтров в localStorage:', error);
    }
  }

  validateFilters(filters) {
    return filters.startDate >= this.DATE_MIN && 
           filters.startDate <= this.DATE_MAX &&
           filters.endDate >= this.DATE_MIN && 
           filters.endDate <= this.DATE_MAX;
  }

  // Utility methods
  reset() {
    this.minLikes = '';
    this.minViews = '';
    this.minComments = '';
    this.startDate = '2025-01-01';
    this.endDate = this.DATE_MAX;
    this.selectedGroups = [];
    this.selectedTimelineEventIds = [];
    this.selectedSession = session_job_types.reduce((acc, job_type) => ({ ...acc, [job_type]: '' }), {});
    this.saveToLocalStorage();
  }

  applyTempGroups() {
    this.selectedGroups = [...this.tempSelectedGroups];
    this.saveToLocalStorage();
  }

  // Методы для работы с группами
  addGroup(groupId) {
    if (!this.selectedGroups.includes(groupId)) {
      this.selectedGroups = [...this.selectedGroups, groupId];
      this.saveToLocalStorage();
    }
  }

  removeGroup(groupId) {
    this.selectedGroups = this.selectedGroups.filter(id => id !== groupId);
    this.saveToLocalStorage();
  }

  toggleGroup(groupId) {
    if (this.selectedGroups.includes(groupId)) {
      this.removeGroup(groupId);
    } else {
      this.addGroup(groupId);
    }
  }

  // Методы для работы с регионами
  addRegionGroups(regionName) {
    const groups = regionsStore.getGroupsByRegion(regionName);
    if (groups.length > 0) {
      const groupIds = groups.map(g => g.group_id);
      this.selectedGroups = [...new Set([...this.selectedGroups, ...groupIds])];
      this.saveToLocalStorage();
    }
  }

  removeRegionGroups(regionName) {
    const groups = regionsStore.getGroupsByRegion(regionName);
    if (groups.length > 0) {
      const groupIds = groups.map(g => g.group_id);
      this.selectedGroups = this.selectedGroups.filter(id => !groupIds.includes(id));
      this.saveToLocalStorage();
    }
  }

  // Методы для экспорта
  getFiltersForAPI() {
    return {
      startDate: this.startDate,
      endDate: this.endDate,
      selectedGroups: this.selectedGroups.map(g => parseInt(g, 10)).filter(Number.isFinite),
      selectedSession: this.selectedSession
    };
  }

  // Методы для валидации
  validateDateRange() {
    const start = new Date(this.startDate);
    const end = new Date(this.endDate);
    return start <= end;
  }

  getValidationErrors() {
    const errors = [];
    
    if (!this.validateDateRange()) {
      errors.push('Дата начала должна быть меньше или равна дате окончания');
    }
    
    if (this.minLikes && parseInt(this.minLikes) < 0) {
      errors.push('Минимальное количество лайков не может быть отрицательным');
    }
    
    if (this.minViews && parseInt(this.minViews) < 0) {
      errors.push('Минимальное количество просмотров не может быть отрицательным');
    }
    
    if (this.minComments && parseInt(this.minComments) < 0) {
      errors.push('Минимальное количество комментариев не может быть отрицательным');
    }
    
    return errors;
  }
}

export default new GlobalFiltersStore();
